import { useCallback, useEffect, useRef, useState } from 'react';

type UseLazyLoadingOptions = {
  hasNextPage: boolean;
  isLoading: boolean;
  fetchNextPage: () => void;
};

export const useSimulationLazyLoading = ({ hasNextPage, isLoading, fetchNextPage }: UseLazyLoadingOptions) => {
  const [shouldShowSkeletons, setShouldShowSkeletons] = useState(false);
  const observerRef = useRef<IntersectionObserver>(
    new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        setShouldShowSkeletons(true);
        fetchNextPage();
      }
    }),
  );

  useEffect(() => {
    if (!hasNextPage) {
      setShouldShowSkeletons(false);
    }
  }, [hasNextPage]);

  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      const observer = observerRef.current;

      observer.disconnect();

      if (!hasNextPage || isLoading || !node) {
        return;
      }

      observer.observe(node);
    },
    [hasNextPage, isLoading],
  );

  useEffect(
    () => () => {
      observerRef.current.disconnect();
    },
    [],
  );

  return { lastItemRef, shouldShowSkeletons };
};
